import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="10223" className="flex gap-8">
        {[1, 2, 3].map(i => <div key={i} className="h-5 w-20 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <Link to={item.href || "#"} className="group relative py-2 text-sm font-medium text-zinc-400 transition-colors hover:text-white font-sans">
              {item.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-cyan-400 to-teal-500 transition-all duration-300 group-hover:w-full" />
            </Link>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center gap-8 p-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-semibold text-zinc-300 transition-colors hover:text-cyan-400 font-serif" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="flex flex-col gap-4 pt-8 w-full max-w-xs">
        <Button variant="ghost" className="w-full justify-center text-zinc-400 hover:text-white hover:bg-white/5" asChild>
          <Link to="#">Log In</Link>
        </Button>
        <Button size="lg" className="w-full justify-center bg-gradient-to-r from-cyan-400 to-teal-500 shadow-[0_0_20px_rgba(34,211,238,0.4)] hover:shadow-[0_0_30px_rgba(34,211,238,0.6)] border-none text-white font-bold tracking-tight transition-all duration-300" asChild>
          <Link to="/ContactPage">Get Started</Link>
        </Button>
      </div>
    </nav>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={cn("dark bg-[#0a0814] text-zinc-100 sticky top-0 z-50 w-full transition-all duration-500", isScrolled ? "bg-[#0a0814]/80 backdrop-blur-xl border-b border-cyan-500/10 shadow-lg" : "bg-[#0a0814] border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-20 items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/HomePage" className="flex items-center gap-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600169/img/engagement_logo.png" alt="RPG-Studios" className="h-9 w-auto object-contain brightness-110" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenu />
            </MenuProvider>
          </div>

          {}
          <div className="hidden md:flex items-center gap-6">
            <Button variant="ghost" className="text-zinc-400 hover:text-white hover:bg-transparent px-0 font-sans transition-colors" asChild>
              
            </Button>
            <Button size="lg" className="bg-gradient-to-r from-cyan-400 to-teal-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all duration-300 border-none rounded-lg px-8 font-bold tracking-tight animate-pulse-slow" asChild>
              <Link to="/ContactPage">Get Started</Link>
            </Button>
          </div>

          {}
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-zinc-100 hover:bg-white/5">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 top-20 z-40 bg-[#0a0814]/98 backdrop-blur-2xl md:hidden animate-in fade-in slide-in-from-top-5 duration-300">
          <div className="container mx-auto h-full overflow-y-auto pb-20">
            <MenuProvider menu_id="16">
              <MobileMenu onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </section>;
}